/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Text,
  Input,
  Textarea,
} from '@chakra-ui/react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import Asterisk from '@/components/common/Asterisk';

import { IProductEditResponse } from '@/services/manageProduct/types';
import { AddOnFormData } from '@/services/addAddons/types';

interface AddonsDetailsSectionProps {
  control: Control<AddOnFormData>;
  errors: FieldErrors<AddOnFormData>;
  product?: IProductEditResponse;
}

const AddonsDetailsSection = ({ control, errors }: AddonsDetailsSectionProps) => {
  return (
    <Box
      border="1px solid"
      borderColor="border.default60"
      bg="background.secondary"
      borderRadius="lg"
    >
      <Flex p={4} direction="column" gap={4}>
        <Text fontSize="md" fontWeight="semibold">
          Addons Details
        </Text>

        <Grid
          templateColumns={{ base: '1fr', md: '1fr 1fr' }}
          templateRows={{ base: 'repeat(3, auto)', md: 'repeat(2, auto)' }}
          gap={6}
        >
          {/* Row 1 - Column 1: Add-on Name */}
          <GridItem>
            <FormControl isInvalid={!!errors.productDetails?.name}>
              <FormLabel
                fontSize="sm"
                color={errors.productDetails?.name ? 'status.error' : 'text.secondary'}
              >
                Add-on Name
                <Asterisk />
              </FormLabel>
              <Controller
                name="productDetails.name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    rounded="lg"
                    placeholder="Enter Add-on Name"
                    bg="background.white"
                  />
                )}
              />
              <FormErrorMessage>{errors.productDetails?.name?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          {/* Row 1 - Column 2: Discount Percentage */}
          <GridItem>
            <FormControl isInvalid={!!errors.productDetails?.discount}>
              <FormLabel fontSize="sm" color="text.secondary">
                Discount Percentage
              </FormLabel>
              <Controller
                name="productDetails.discount"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter Discount % (e.g. 15)"
                    rounded="lg"
                    bg="background.white"
                    onClick={() => {
                      if (field.value === 0) field.onChange('');
                    }}
                  />
                )}
              />
              <FormErrorMessage>{errors.productDetails?.discount?.message}</FormErrorMessage>
            </FormControl>
          </GridItem>

          {/* Row 2 - Full width: Description */}
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <FormControl isInvalid={!!errors.productDetails?.shortDescription}>
              <FormLabel
                fontSize="sm"
                color={errors.productDetails?.shortDescription ? 'status.error' : 'text.secondary'}
              >
                Description
                <Asterisk />
              </FormLabel>
              <Controller
                name="productDetails.shortDescription"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Enter description"
                    rounded="lg"
                    bg="background.white"
                    minH={{ base: '100px', md: '140px' }}
                  />
                )}
              />
              <FormErrorMessage>
                {errors.productDetails?.shortDescription?.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>
      </Flex>
    </Box>
  );
};

export default AddonsDetailsSection;
